import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, ShoppingBag, Search, UserRound } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { SearchOverlay } from "@/components/SearchOverlay";
import { TopBar } from "@/components/TopBar";
const NAV = [
  { to: "/", label: "Accueil", en: "Home" },
  { to: "/catalogue", label: "Catalogue", en: "Catalogue" },
  { to: "/services", label: "Accompagnement", en: "Consulting" },
  { to: "/formations", label: "Formations", en: "Training" },
  { to: "/conseils", label: "Conseils", en: "Insights" },
  { to: "/equipe", label: "Équipe", en: "Team" },
  { to: "/contact", label: "Contact", en: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { lang, toggle, t } = useLang();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
      style={{
        background: scrolled ? "rgba(245,245,240,0.72)" : "transparent",
        backdropFilter: scrolled ? "blur(18px) saturate(150%)" : "none",
        borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
      }}
      data-testid="site-header"
    >
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[112px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/aldol-logo.png" alt="ALDOL-SOLUTIONS" className="h-[104px] w-[104px] object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="link-hover text-sm font-medium"
              style={{ color: loc.pathname === n.to ? "var(--orange)" : "var(--ink)" }}
            >
              {t(n.label, n.en)}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={toggle} className="px-2 py-1 text-xs font-bold tracking-wider" style={{ border: "1px solid var(--border)" }} data-testid="lang-toggle-btn" aria-label="Changer de langue">
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button onClick={() => setSearchOpen(true)} className="p-2" data-testid="header-search-btn" aria-label="Rechercher">
            <Search size={21} />
          </button>
          <Link to="/espace-client" className="p-2" data-testid="header-account-btn" aria-label="Espace client">
            <UserRound size={21} />
          </Link>
          <button onClick={() => setCartOpen(true)} className="relative p-2" data-testid="header-cart-btn" aria-label="Panier" style={{ display: count > 0 ? "block" : "none" }}>
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full"
                style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid="header-cart-count">{count}</span>
            )}
          </button>
          <Link to="/contact" data-testid="header-devis-btn"
            className="btn-primary px-5 py-2.5 text-sm font-semibold">
            {t("Demander un devis", "Request a quote")}
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-1">
          <button onClick={toggle} className="px-2 py-1 text-xs font-bold" style={{ border: "1px solid var(--border)" }} data-testid="mobile-lang-toggle" aria-label="Changer de langue">
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button onClick={() => setSearchOpen(true)} className="p-2" data-testid="mobile-search-btn" aria-label="Rechercher">
            <Search size={21} />
          </button>
          <button onClick={() => setCartOpen(true)} className="relative p-2" data-testid="mobile-cart-btn" aria-label="Panier" style={{ display: count > 0 ? "block" : "none" }}>
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full"
                style={{ background: "var(--orange)", color: "var(--bone)" }}>{count}</span>
            )}
          </button>
          <button className="p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden px-5 pb-6 pt-2" style={{ background: "var(--bone)", borderBottom: "1px solid var(--border)" }} data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="text-lg font-medium font-display"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                {t(n.label, n.en)}
              </Link>
            ))}
            <Link to="/contact" className="btn-primary px-5 py-3 text-center font-semibold mt-2">
              {t("Demander un devis", "Request a quote")}
            </Link>
          </div>
        </div>
      )}
      <SearchOverlay open={searchOpen} setOpen={setSearchOpen} />
    </header>
  );
}
